import React from 'react';
import { Button } from 'react-bootstrap';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { format, eachDayOfInterval, getDay, startOfMonth, endOfMonth, startOfWeek, endOfWeek, isSameDay, isWithinInterval, addMonths, subMonths, isValid } from 'date-fns';
import { getAllTimeSlots, getJsDayIndex } from '../../utils/roleHelpers';

const ScheduleCalendar = ({ 
    calDate, setCalDate, 
    scheduleStartDate, scheduleEndDate, 
    scheduleStartTime, scheduleEndTime, castingInterval, 
    watchedWeekDaysOff, dateSpecificOffSlots, toggleSlotAvailability, isCurrentVideo 
}) => {

    const safeSDate = isValid(scheduleStartDate) ? scheduleStartDate : null; 
    const safeEDate = isValid(scheduleEndDate) ? scheduleEndDate : null;

    const currentCalDate = isValid(calDate) ? calDate : (safeSDate || new Date());

    const currentMonthStart = startOfMonth(currentCalDate);
    const currentMonthEnd = endOfMonth(currentCalDate);

    // ✅ Align grid to start from Sunday of the week
    const calendarStart = startOfWeek(currentMonthStart, { weekStartsOn: 0 });
    const calendarEnd = endOfWeek(currentMonthEnd, { weekStartsOn: 0 });

    const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
    
    // ✅ Uses getJsDayIndex for proper day off index
    // const offDayIndexes = (watchedWeekDaysOff || []).map(d => getJsDayIndex(d)).filter(i => i !== -1); 
    const normalizeWeekDaysOff = (input) => {
    if (!input) return [];
    if (Array.isArray(input)) return input;
    if (typeof input === 'string') return input.split(';');
    return [input];
};
const offDayIndexes = normalizeWeekDaysOff(watchedWeekDaysOff)
    .map(d => getJsDayIndex(d))
    .filter(i => i !== -1);
    
    const allTimeSlots = getAllTimeSlots({ 
        start_time: scheduleStartTime, 
        end_time: scheduleEndTime, 
        casting_time: castingInterval,
    }, isCurrentVideo);

    // ✅ FIXED: Month Navigation
    const handlePreviousMonth = () => {
        let newCalDate = startOfMonth(subMonths(currentCalDate, 1));
        if (safeSDate && newCalDate < startOfMonth(safeSDate)) {
            newCalDate = startOfMonth(safeSDate);
        }
        setCalDate(newCalDate);
    };

    const handleNextMonth = () => {
        let newCalDate = startOfMonth(addMonths(currentCalDate, 1));
        if (safeEDate && newCalDate > startOfMonth(safeEDate)) {
            newCalDate = startOfMonth(safeEDate);
        }
        setCalDate(newCalDate);
    };

    const isPrevMonthDisabled = safeSDate && startOfMonth(currentCalDate) <= startOfMonth(safeSDate);
    const isNextMonthDisabled = safeEDate && startOfMonth(currentCalDate) >= startOfMonth(safeEDate);

    // ✅ FIXED: Clamping allows viewing valid month without forcing day clamping back
    React.useEffect(() => {
        if (safeSDate && safeEDate) {
            const currentMonth = startOfMonth(currentCalDate);
            const minMonth = startOfMonth(safeSDate);
            const maxMonth = startOfMonth(safeEDate);

            if (currentMonth < minMonth) {
                setCalDate(safeSDate);
            } else if (currentMonth > maxMonth) {
                setCalDate(safeEDate);
            }
        }
    }, [safeSDate, safeEDate]);

    return (
        <div className="animate-fade-in">
            <h6 className="text-white mb-3 d-flex justify-content-between align-items-center">
                <Button variant='link' className='text-white p-1' onClick={handlePreviousMonth} disabled={isPrevMonthDisabled}><FaChevronLeft/></Button>
                {format(currentCalDate, 'MMMM yyyy')}
                <Button variant='link' className='text-white p-1' onClick={handleNextMonth} disabled={isNextMonthDisabled}><FaChevronRight/></Button>
            </h6>
            <div className="custom-calendar-grid mb-4">
                {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => <div key={d} className="cal-header">{d}</div>)}
                {days.map((day, i) => {
                    const dayIndex = getDay(day);
                    const isWeekDayOff = offDayIndexes.includes(dayIndex);
                    
                    const isCurrentMonth = day >= currentMonthStart && day <= currentMonthEnd;
                    const inScheduleRange = safeSDate && safeEDate && isWithinInterval(day, { start: safeSDate, end: safeEDate });
                    const isSelected = isValid(currentCalDate) && isSameDay(currentCalDate, day); 

                    let classNames = 'cal-day ';
                    
                    if (!isCurrentMonth) {
                        classNames += 'out-month opacity-25 ';
                    } else if (!inScheduleRange) {
                        classNames += 'out-range ';
                    } else if (isWeekDayOff) {
                        classNames += 'disabled-day ';
                    } else if (isSelected) {
                        classNames += 'active ';
                    }

                    return (
                        <div key={i} 
                             className={classNames} 
                             onClick={() => { 
                                 if (isCurrentMonth && inScheduleRange && !isWeekDayOff) {
                                     setCalDate(day); 
                                 }
                             }}
                        >
                            {format(day, 'd')}
                        </div>
                    );
                })}
            </div>
            <h6 className="text-white small mb-2">Time Slots ({format(currentCalDate, 'dd MMM')})</h6>
            <div className="slot-legend">
                <div className="legend-item"><div className="legend-color bg-success"></div> Available</div>
                <div className="legend-item"><div className="legend-color bg-danger"></div> Break</div>
            </div>
            <div className="time-slot-grid-calculated d-flex flex-wrap gap-2">
                {allTimeSlots.length > 0 ? allTimeSlots.map((time) => { 
                    const isBreak = (dateSpecificOffSlots[format(currentCalDate, 'yyyy-MM-dd')] || []).includes(time); 
                    return (
                        <div key={time} onClick={() => toggleSlotAvailability(time)} className={`time-slot-box ${isBreak ? 'slot-break' : 'slot-available'}`} title={isBreak ? "Mark Available" : "Mark Break"}>
                            {time}
                        </div>
                    ); 
                }) : <small className="text-muted">No slots available for this time range and interval.</small>}
            </div>
        </div>
    );
};

export default ScheduleCalendar;